import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { CmpTextFieldV1 } from "../../../Component/Common/ToolBox/ToolBoxV1";
import { inv10_INV10ExcelImport_GETROW } from "../../../Server/ApiV2";
import { fAlertToast } from "../../../Utility/Utilitys";
import { CmpCheckboxField } from "../../../Component/Common/ToolBox/ToolBox";

const LoadFromExcel = ({ open, onClose, VocTyp = "", VocNo = "" }) => {
  const [DeleteExist, setDeleteExist] = useState(false);
  const [fileName, setFileName] = useState("");
  let vMediaQuery = useMediaQuery("(max-width:750px)");
  const [fileData, setFileData] = useState([]); // Parsed data from the file
  const [gridData, setGridData] = useState([]); // Data for the DataGrid
  const [selectedRows, setSelectedRows] = useState([]); // Selected rows in the DataGrid

const modalClose=()=>{
  onClose();
  setGridData([]);
  setFileName([]);
  setDeleteExist(false);
}

  // Handle file selection and parse Excel file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1, // Read data with headers
        });

        // Convert rows to array of objects based on the first row as headers
        const [headers, ...rows] = sheetData;
        const parsedData = rows.map((row) =>
          headers.reduce((acc, header, index) => {
            acc[header] = row[index];
            return acc;
          }, {})
        );

        // Store the parsed data without updating the grid yet
        setFileData(parsedData);
      };
      reader.readAsBinaryString(file);
    }
  };

  // Handle download click to update the grid with parsed data
  const handleDownload = () => {
    if (fileName == "") {
      fAlertToast("FAILED", "Please select file");
      return;
    } else {
      setGridData(
        fileData.map((row, index) => ({
          id: index + 1, // Ensure each row has a unique ID for selection
          ...row,
        }))
      ); // Update the grid with parsed file data
    }
    // setGridData(fileData); // Update the grid with parsed file data
  };

  // Handle upload click
  const handleUpload = async () => {
    // Send selected rows to API
    if (selectedRows.length == 0) {
      fAlertToast("FAILED", "Select atleat one item");
      return;
    } else {
      console.log("Selected Rows:", selectedRows);

      const items = selectedRows.map((rowId) => {
        const row = gridData.find((item) => item.id === rowId);
        return {
          barcode: row.Barcode,
          qty: row.Qty,
          ItemName: row.ItemName || "",
        };
      });

      const payload = {
        A_VocTyp: VocTyp,
        A_VocNo: VocNo,
        DeleteExist: DeleteExist ? "Y":"N",
        items,
      };
      console.log(payload);
      const resp = await inv10_INV10ExcelImport_GETROW(payload);

      if (resp?.status === 200) {
        console.log(resp);
        modalClose()
        fAlertToast("SUCCESS", "Updated successfully");
      }
    }
  };

  // Define DataGrid columns dynamically
  const columns = [
    { field: "id", headerName: "ID", hide: true }, // Explicitly hide the `id` column
    ...Object.keys(gridData[0] || {})
      .filter((key) => key !== "id") // Dynamically exclude the `id` field
      .map((key) => ({
        field: key,
        headerName: key.replace(/_/g, " "), // Format header name
        width: 200,
      })),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          padding: "4px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="div">
          Load From Excel
        </Typography>
        <IconButton
          onClick={() => {
            modalClose()
          }}
          sx={{ color: "white" }}
        >
          <CloseIcon
            onClick={() => {
              modalClose()
            }}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="center" paddingTop={2}>
          <Grid item xs={6} sm={3}>
            <CmpTextFieldV1
              xLabelText="VocType"
              xSize="small"
              xValue={VocTyp}
              xType={"text"}
              xName={"VocType"}
              xReadOnly={true}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <CmpTextFieldV1
              xLabelText="VocNo"
              xSize="small"
              xValue={VocNo}
              xType={"text"}
              xName={"VocNo"}
              xReadOnly={true}
            />
          </Grid>
          <Grid item xs={12} sm={2} justifyContent={"start"}>
            <CmpCheckboxField
              xChecked={DeleteExist}
              xOnChange={(e) => {
                setDeleteExist(e.target.checked);
              }}
              xDisabled={false}
              xName={"DeleteExist"}
              xNameField={"DeleteExist"}
              xError={false}
              xErrorMessage={""}
            />
          </Grid>
          <Grid item xs={6} sm={2} textAlign={"center"}>
            <Button variant="contained" component="label" size="small">
              Browse
              <input
                type="file"
                hidden
                accept=".xls,.xlsx"
                onChange={handleFileUpload}
              />
            </Button>
          </Grid>
          <Grid item xs={6} sm={2} textAlign={"center"}>
            <Button
              variant="contained"
              size="small"
              onClick={handleDownload}
              disabled={fileData.length === 0} // Disable button if no file is uploaded
            >
              Download
            </Button>
          </Grid>
        </Grid>

        <div
          style={{
            height: vMediaQuery ? 280 : 400,
            width: "100%",
            marginTop: "20px",
          }}
        >
          <DataGrid
            rows={gridData.map((row, index) => ({ id: index + 1, ...row }))}
            columns={columns}
            checkboxSelection
            onSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
            hideFooter={gridData.length < 100}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={selectedRows.length === 0} // Disable button if no rows are selected
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadFromExcel;
